import { Inject, Injectable } from '@angular/core';
import {
	API_BASE_URL_BACKEND,
	DcBaseInitializationService,
	DcCoreBackendBasePath,
	toImmutableMap,
} from '@datachain/ui-sdk/common';
import { OrderedMap } from 'immutable';

import { DxListsStorage } from '../core/dx-lists.storage';
import { DcDxGrids } from '../core/dx-types';
import { ProjectStorage } from '../core/project.storage';
import { TagAdapter } from '../core/tag.adapter';
import {
	HdGraphSubTypeModel,
	HdGraphTypeModel,
	IChartResponse,
} from '../requesters/chart/chart.model';
import { HdIcons } from '../ui/app-hd.icons';
import { ChartItemEntity } from './components/chart-list/chart-item.entity';
import {
	ChartListEntity,
	IChartListExtra,
} from './components/chart-list/chart-list.entity';
import { HdGraphSubType, HdGraphType } from './domain/hd-graph.domain';

@Injectable()
export class ChartAdapter {
	public constructor(
		private readonly tagAdapter: TagAdapter,
		private readonly dxListsStorage: DxListsStorage,
		@Inject(API_BASE_URL_BACKEND)
		private readonly apiBase: DcCoreBackendBasePath,
		private readonly userSettings: ProjectStorage
	) {}

	public generateChartList(
		response: ReadonlyArray<IChartResponse>
	): ChartListEntity {
		const columns = this.getChartColumns();
		const columnsTuples = response
			.reduce<Array<ChartItemEntity>>((acc, item) => {
				const temp = this.generateChartItemEntity(item);
				acc.push(temp);
				return acc;
			}, [])
			.sort((a, b) => {
				if (!a.updateDate) {
					throw new Error('Date is null!');
				}
				if (!b.updateDate) {
					throw new Error('Date is null!');
				}

				return b.updateDate.getTime() - a.updateDate.getTime();
			});

		const orderedMap = OrderedMap<string, ChartItemEntity>(
			columnsTuples.map((item) => [`${item.id}`, item])
		);
		return columns.setElements(orderedMap);
	}

	public getHdGraphTypeModel(
		hdGraphType: HdGraphType,
		hdGraphSubType: HdGraphSubType | undefined
	): HdGraphTypeModel {
		if (hdGraphType === HdGraphType.Barline) {
			return HdGraphTypeModel.BARLINE;
		}

		if (hdGraphType === HdGraphType.Funnel) {
			return HdGraphTypeModel.FUNNEL;
		}

		if (hdGraphType === HdGraphType.Indicator) {
			return HdGraphTypeModel.INDICATOR;
		}

		if (hdGraphType === HdGraphType.Pie) {
			return HdGraphTypeModel.PIE;
		}

		if (hdGraphType === HdGraphType.Range) {
			return HdGraphTypeModel.RANGE;
		}

		if (hdGraphType === HdGraphType.Sankey) {
			return HdGraphTypeModel.SANKEY;
		}

		if (hdGraphType === HdGraphType.Scatter) {
			return HdGraphTypeModel.SCATTER;
		}

		if (hdGraphType === HdGraphType.Treemap) {
			return HdGraphTypeModel.TREEMAP;
		}

		if (hdGraphType === HdGraphType.Gauge) {
			if (hdGraphSubType === HdGraphSubType.Bar) {
				return HdGraphTypeModel.GAUGE_BAR;
			}

			if (hdGraphSubType === HdGraphSubType.Circular) {
				return HdGraphTypeModel.GAUGE_CIRCULAR;
			}

			if (hdGraphSubType === HdGraphSubType.Linear) {
				return HdGraphTypeModel.GAUGE_LINEAR;
			}
		}

		throw new Error(`Unrecognized type: ${hdGraphType}`);
	}

	private getChartColumns(): ChartListEntity {
		let availableCols: Array<IChartListExtra> = [
			{
				field: 'isActive',
				alias: $localize`:i18n=@@general.status:`,
				type: 'boolean',
				isSearchable: false,
				isFilterable: false,
				hasHeaderFilter: true,
				visibleIndex: 0,
				width: '5.5rem',
			},
			{
				field: 'code',
				alias: $localize`:i18n=@@chart.list.column.code:`,
				type: 'string',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: true,
				visible: false,
				visibleIndex: 1,
			},
			{
				field: 'label',
				alias: $localize`:i18n=@@general.column.label:`,
				type: 'string',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: false,
				visibleIndex: 2,
				width: '20%',
			},
			{
				field: 'projectLabel',
				alias: $localize`:i18n=@@chart.list.column.project:`,
				type: 'string',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: true,
				visibleIndex: 3,
			},
			{
				field: 'icon',
				alias: '',
				type: '',
				isSearchable: false,
				hasHeaderFilter: true,
				isVisible: false,
				visibleIndex: 4,
			},
			{
				field: 'type',
				alias: $localize`:i18n=@@general.column.type:`,
				type: 'string',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: true,
				visibleIndex: 4,
				width: '15%',
			},
			{
				field: 'tags',
				alias: $localize`:i18n=@@chart.list.column.tags:`,
				type: '',
				isSearchable: false,
				hasHeaderFilter: true,
				visibleIndex: 5,
			},
			{
				field: 'description',
				alias: $localize`:i18n=@@chart.list.column.description:`,
				type: 'string',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: false,
				visibleIndex: 6,
			},
			{
				field: 'creationDate',
				alias: $localize`:i18n=@@chart.list.column.creationDate:`,
				type: 'datetime',
				format: 'yyyy/MM/dd',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: false,
				visible: false,
				visibleIndex: 7,
			},
			{
				field: 'createdByAvatar',
				alias: $localize`:i18n=@@chart.list.column.createdBy:`,
				type: 'string',
				isSearchable: false,
				isFilterable: true,
				hasHeaderFilter: true,
				visible: false,
				visibleIndex: 8,
			},
			{
				field: 'updateDate',
				alias: $localize`:i18n=@@chart.list.column.updateDate:`,
				type: 'datetime',
				format: 'yyyy/MM/dd',
				isSearchable: true,
				isFilterable: true,
				hasHeaderFilter: false,
				visibleIndex: 9,
				width: '10rem',
			},
			{
				field: 'updatedByAvatar',
				alias: $localize`:i18n=@@chart.list.column.updatedBy:`,
				type: 'string',
				isSearchable: false,
				isFilterable: true,
				hasHeaderFilter: true,
				visibleIndex: 10,
			},
		];

		availableCols = this.dxListsStorage.synchronize<
			IChartListExtra,
			ChartItemEntity
		>(DcDxGrids.ChartListStandalone, availableCols);
		return ChartListEntity.build(availableCols);
	}

	private generateChartItemEntity(response: IChartResponse): ChartItemEntity {
		const resourceUrl =
			DcBaseInitializationService.getResource('PICTOGRAMME_IMAGE');
		const elemProjectId = response.project?.id ?? -1;
		const belongsToProject = this.userSettings.getProjectId() === elemProjectId;
		const instance = ChartItemEntity.build({
			id: response.id,
			isInSameProject: belongsToProject,
			label: response.label ?? '',
			code: response.code ?? '',
			description: response.description ?? '',
			projectLabel: response.project?.label ?? '',
			projectPictoUrl: `${this.apiBase.getBasePath()}/${resourceUrl}?id=${
				response.project?.icon_id ?? -1
			}`,
			icon: undefined,
			type: response.type ? this.getHdGraphType(response.type) : undefined,
			isProjectPublic: response.project?.is_public ?? false,
			updateDate:
				response.update_date === undefined
					? new Date(response.creation_date ?? '')
					: new Date(response.update_date),
			creationDate: response.creation_date
				? new Date(response.creation_date)
				: null,
			createdBy: response.created_by
				? `${response.created_by.first_name} ${response.created_by.last_name}`
				: '',
			updatedBy:
				response.updated_by === undefined
					? response.created_by
						? `${response.created_by.first_name} ${response.created_by.last_name}`
						: ''
					: `${response.updated_by.first_name} ${response.updated_by.last_name}`,
			tags: this.tagAdapter.generateResponseMetadataTag(response.tags ?? []),
			isActive: response.active ?? false,
			pictogramUrl: `${this.apiBase.getBasePath()}/${resourceUrl}?id=${
				response.icon_id ?? -1
			}`,
		});

		if (response.type === HdGraphTypeModel.BARLINE) {
			if (response.sub_type === HdGraphSubTypeModel.LINE) {
				return instance
					.setIcon(HdIcons.LineChart)
					.setSubType(HdGraphSubType.Line);
			}

			if (response.sub_type === HdGraphSubTypeModel.BAR) {
				return instance
					.setIcon(HdIcons.BarChart)
					.setSubType(HdGraphSubType.Bar);
			}

			if (response.sub_type === HdGraphSubTypeModel.AREA) {
				return instance
					.setIcon(HdIcons.AreaChart)
					.setSubType(HdGraphSubType.Area);
			}
		}

		if (response.type === HdGraphTypeModel.PIE) {
			return instance.setIcon(HdIcons.PieChart);
		}

		if (response.type === HdGraphTypeModel.FUNNEL) {
			return instance.setIcon(HdIcons.FunnelChart);
		}

		if (response.type === HdGraphTypeModel.GAUGE_BAR) {
			return instance
				.setIcon(HdIcons.GaugeBarChart)
				.setSubType(HdGraphSubType.Bar);
		}

		if (response.type === HdGraphTypeModel.GAUGE_CIRCULAR) {
			return instance
				.setIcon(HdIcons.GaugeCircularChart)
				.setSubType(HdGraphSubType.Circular);
		}

		if (response.type === HdGraphTypeModel.GAUGE_LINEAR) {
			return instance
				.setIcon(HdIcons.GaugeLinearChart)
				.setSubType(HdGraphSubType.Linear);
		}

		if (response.type === HdGraphTypeModel.TREEMAP) {
			return instance.setIcon(HdIcons.TreemapChart);
		}

		if (response.type === HdGraphTypeModel.SANKEY) {
			return instance.setIcon(HdIcons.SankeyChart);
		}

		if (response.type === HdGraphTypeModel.SCATTER) {
			return instance.setIcon(HdIcons.ScatterChart);
		}

		if (response.type === HdGraphTypeModel.RANGE) {
			return instance.setIcon(HdIcons.RangeChart);
		}

		if (response.type === HdGraphTypeModel.INDICATOR) {
			return instance.setIcon(HdIcons.IndicatorChart);
		}

		return instance;
	}
	private getHdGraphType(
		hdGraphType: HdGraphTypeModel
	): HdGraphType | undefined {
		switch (hdGraphType) {
			case HdGraphTypeModel.GAUGE_BAR:
			case HdGraphTypeModel.GAUGE_CIRCULAR:
			case HdGraphTypeModel.GAUGE_LINEAR:
				return HdGraphType.Gauge;
				break;
			case HdGraphTypeModel.BARLINE:
				return HdGraphType.Barline;
				break;
			case HdGraphTypeModel.PIE:
				return HdGraphType.Pie;
				break;
			case HdGraphTypeModel.FUNNEL:
				return HdGraphType.Funnel;
				break;
			case HdGraphTypeModel.TREEMAP:
				return HdGraphType.Treemap;
				break;
			case HdGraphTypeModel.SANKEY:
				return HdGraphType.Sankey;
				break;
			case HdGraphTypeModel.SCATTER:
				return HdGraphType.Scatter;
				break;
			case HdGraphTypeModel.RANGE:
				return HdGraphType.Range;
				break;
			case HdGraphTypeModel.INDICATOR:
				return HdGraphType.Indicator;
				break;
			default:
				return undefined;
				break;
		}
	}
}
